import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

const read = <T>(key: string, defaultValue: T): T => {
  const raw = localStorage.getItem(key);
  if (raw === null) {
    return defaultValue;
  }
  try {
    return JSON.parse(raw) as T;
  } catch {
    return defaultValue;
  }
};

export const usePersistentState = <T>(
  key: string,
  defaultValue: T,
): [T, Dispatch<SetStateAction<T>>, () => void] => {
  const [value, setValue] = useState<T>(() => read(key, defaultValue));
  const defaultValueRef = useRef(defaultValue);

  const clear = useCallback(() => {
    localStorage.removeItem(key);
    setValue(defaultValueRef.current);
  }, [key]);

  useEffect(() => {
    if (JSON.stringify(value) === JSON.stringify(defaultValueRef.current)) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(value));
  }, [value, key]);

  return [value, setValue, clear];
};
